import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Copy,
  LogOut,
  Mail,
  RefreshCw,
  Search,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "archived", label: "Archived" },
];

const FILTERS = [
  { id: "all", label: "All" },
  { id: "new", label: "New" },
  { id: "contacted", label: "Contacted" },
  { id: "archived", label: "Archived" },
];

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusPill({ status }) {
  const map = {
    new: "bg-[#D67229] text-white",
    contacted: "bg-[#0F4C5C] text-white",
    archived: "bg-[#D3D9DE] text-[#2C333A]",
  };
  return (
    <span
      data-testid={`lead-status-pill-${status}`}
      className={`inline-flex items-center font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${
        map[status] || map.new
      }`}
    >
      {status}
    </span>
  );
}

export default function AdminLeads() {
  const { user, authApi, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, archived: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        authApi.get("/admin/leads"),
        authApi.get("/admin/leads/stats"),
      ]);
      setLeads(leadsRes.data || []);
      setStats(statsRes.data || stats);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 401 || code === 403) {
        toast.error("Admin access required.");
        navigate("/dashboard", { replace: true });
      } else {
        toast.error("Couldn't load leads.");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authApi, navigate]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const filteredLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (filter !== "all" && (l.status || "new") !== filter) return false;
      if (!q) return true;
      return [l.name, l.company, l.email, l.message].some((v) =>
        (v || "").toLowerCase().includes(q)
      );
    });
  }, [leads, filter, search]);

  const updateLead = async (lead, patch) => {
    setUpdatingId(lead.id);
    try {
      const res = await authApi.patch(`/admin/leads/${lead.id}`, patch);
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? res.data : l)));
      if (patch.status) {
        const delta = { ...stats };
        delta[lead.status || "new"] = Math.max(0, (delta[lead.status || "new"] || 0) - 1);
        delta[patch.status] = (delta[patch.status] || 0) + 1;
        setStats(delta);
        toast.success(`Marked as ${patch.status}`);
      } else if (patch.notes !== undefined) {
        toast.success("Notes saved");
      }
    } catch (_e) {
      toast.error("Update failed");
    } finally {
      setUpdatingId(null);
    }
  };

  const copyEmail = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success("Email copied");
    } catch (_e) {
      toast.error("Could not copy");
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F3EE] text-[#111418]" data-testid="admin-leads-page">
      <header className="border-b border-[#D3D9DE] bg-[#F4F3EE]/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="gp-container h-16 md:h-20 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              data-testid="admin-back-button"
              className="inline-flex items-center gap-2 group"
            >
              <ArrowLeft size={14} className="text-[#2C333A] group-hover:text-[#D67229] transition-colors" />
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#2C333A] group-hover:text-[#D67229] transition-colors">
                Dashboard
              </span>
            </button>
            <div className="hidden sm:flex items-center gap-2">
              <span aria-hidden className="inline-block h-2 w-2 bg-[#D67229]" />
              <span className="font-display text-xl font-bold tracking-tight">
                GushPay <span className="text-[#0F4C5C] italic">/ admin</span>
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#2C333A] hidden md:inline">
              {user?.email}
            </span>
            <button
              type="button"
              onClick={async () => {
                await logout();
                navigate("/", { replace: true });
              }}
              data-testid="admin-logout"
              className="gp-btn gp-btn-ghost"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="gp-container py-12 md:py-16">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <span className="gp-overline">// Inbound pipeline</span>
            <h1 className="mt-4 font-display text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
              Demo <span className="italic text-[#0F4C5C]">requests.</span>
            </h1>
            <p className="mt-3 text-[#2C333A] max-w-xl">
              Every operator who clicked "Schedule a Demo." Mark as contacted once you reach
              out, add a note, or archive when closed.
            </p>
          </div>
          <button
            type="button"
            onClick={fetchAll}
            data-testid="admin-refresh"
            className="gp-btn gp-btn-ghost"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { k: "total", label: "Total", color: "#111418" },
            { k: "new", label: "New", color: "#D67229" },
            { k: "contacted", label: "Contacted", color: "#0F4C5C" },
            { k: "archived", label: "Archived", color: "#8593A1" },
          ].map((s) => (
            <div
              key={s.k}
              data-testid={`admin-stat-${s.k}`}
              className="bg-white border border-[#D3D9DE] p-5"
            >
              <p className="gp-overline" style={{ color: s.color }}>
                {s.label}
              </p>
              <p className="mt-2 font-display text-3xl md:text-4xl text-[#111418] leading-none">
                {stats[s.k] ?? 0}
              </p>
            </div>
          ))}
        </div>

        {/* Filters + search */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                data-testid={`admin-filter-${f.id}`}
                className={`h-9 px-4 font-mono text-[11px] uppercase tracking-[0.2em] border transition-colors ${
                  filter === f.id
                    ? "bg-[#0F4C5C] text-white border-[#0F4C5C]"
                    : "bg-transparent text-[#2C333A] border-[#D3D9DE] hover:border-[#111418]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="md:ml-auto flex items-center gap-2 bg-white border border-[#D3D9DE] px-3 h-10 md:min-w-[280px]">
            <Search size={14} className="text-[#8593A1]" />
            <input
              data-testid="admin-search"
              type="search"
              placeholder="Search name, company, email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-transparent outline-none text-sm flex-1 placeholder:text-[#8593A1]"
            />
          </div>
        </div>

        {/* Leads list */}
        <div className="bg-white border border-[#D3D9DE]" data-testid="admin-leads-list">
          {loading ? (
            <div className="px-6 py-12 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-[#8593A1]">
              Loading leads…
            </div>
          ) : filteredLeads.length === 0 ? (
            <div className="px-6 py-16 text-center" data-testid="admin-leads-empty">
              <p className="gp-overline">// No leads match</p>
              <p className="mt-4 font-display text-xl text-[#111418]">
                Nothing here yet.
              </p>
              <p className="mt-2 text-sm text-[#2C333A]">
                When a new demo request comes in, it'll appear at the top of this list.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-[#E9ECEF]">
              {filteredLeads.map((lead) => {
                const expanded = expandedId === lead.id;
                return (
                  <li
                    key={lead.id}
                    data-testid={`admin-lead-row-${lead.id}`}
                    className="px-5 md:px-8 py-5"
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:gap-6">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-3">
                          <p className="font-display text-lg md:text-xl text-[#111418] truncate">
                            {lead.name}{" "}
                            <span className="text-[#8593A1] font-sans text-sm">·</span>{" "}
                            <span className="text-[#0F4C5C]">{lead.company}</span>
                          </p>
                          <StatusPill status={lead.status || "new"} />
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-[#2C333A]">
                          <span className="inline-flex items-center gap-2">
                            <Mail size={12} className="text-[#0F4C5C]" />
                            <a
                              href={`mailto:${lead.email}`}
                              className="hover:text-[#D67229] transition-colors"
                            >
                              {lead.email}
                            </a>
                            <button
                              type="button"
                              onClick={() => copyEmail(lead.email)}
                              aria-label="Copy email"
                              data-testid={`admin-lead-copy-${lead.id}`}
                              className="text-[#8593A1] hover:text-[#0F4C5C] transition-colors"
                            >
                              <Copy size={12} />
                            </button>
                          </span>
                          {lead.well_count ? (
                            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#7A3906]">
                              · {lead.well_count} wells
                            </span>
                          ) : null}
                          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#8593A1]">
                            {formatDate(lead.created_at)}
                          </span>
                        </div>
                        {lead.message ? (
                          <button
                            type="button"
                            onClick={() => setExpandedId(expanded ? null : lead.id)}
                            data-testid={`admin-lead-expand-${lead.id}`}
                            className="mt-3 inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.2em] text-[#0F4C5C] hover:text-[#D67229] transition-colors"
                          >
                            <ChevronDown
                              size={12}
                              className={`transition-transform ${expanded ? "rotate-180" : ""}`}
                            />
                            {expanded ? "Hide message" : "Show message"}
                          </button>
                        ) : null}
                      </div>

                      <div className="mt-4 md:mt-0 flex items-center gap-3">
                        <div className="relative">
                          <select
                            value={lead.status || "new"}
                            disabled={updatingId === lead.id}
                            data-testid={`admin-lead-status-${lead.id}`}
                            onChange={(e) => updateLead(lead, { status: e.target.value })}
                            className="appearance-none bg-white border border-[#111418] pr-8 pl-3 h-10 font-mono text-[11px] uppercase tracking-[0.2em] text-[#111418] cursor-pointer hover:bg-[#F4F3EE] transition-colors"
                          >
                            {STATUS_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                          <ChevronDown
                            size={12}
                            className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-[#2C333A]"
                          />
                        </div>
                      </div>
                    </div>

                    {expanded && (
                      <div
                        data-testid={`admin-lead-expanded-${lead.id}`}
                        className="mt-4 pl-4 border-l-2 border-[#D67229] bg-[#F4F3EE]/60 p-4"
                      >
                        {lead.message ? (
                          <p className="text-[15px] text-[#1C2026] leading-relaxed">
                            "{lead.message}"
                          </p>
                        ) : (
                          <p className="text-sm text-[#8593A1] italic">No message attached.</p>
                        )}
                        <NoteEditor
                          lead={lead}
                          onSave={(notes) => updateLead(lead, { notes })}
                          saving={updatingId === lead.id}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8593A1]">
          Showing {filteredLeads.length} of {leads.length} · Admins: {ADMIN_HINT}
        </p>
      </main>
    </div>
  );
}

const ADMIN_HINT = "set ADMIN_EMAILS env var";

function NoteEditor({ lead, onSave, saving }) {
  const [value, setValue] = useState(lead.notes || "");
  const dirty = value !== (lead.notes || "");
  return (
    <div className="mt-4">
      <label
        htmlFor={`note-${lead.id}`}
        className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#2C333A] mb-1"
      >
        Internal notes
      </label>
      <textarea
        id={`note-${lead.id}`}
        data-testid={`admin-lead-notes-${lead.id}`}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={2}
        className="w-full bg-white border border-[#D3D9DE] focus:border-[#0F4C5C] outline-none px-3 py-2 text-[14px] text-[#111418] resize-y transition-colors"
        placeholder="e.g. Followed up via email on Dec 3 — sent demo deck."
      />
      <div className="mt-2 flex items-center gap-3">
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={() => onSave(value)}
          data-testid={`admin-lead-notes-save-${lead.id}`}
          className="gp-btn gp-btn-primary disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save note"}
        </button>
        {dirty && (
          <button
            type="button"
            onClick={() => setValue(lead.notes || "")}
            data-testid={`admin-lead-notes-cancel-${lead.id}`}
            className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#2C333A] hover:text-[#D67229] transition-colors"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
